/**
 * Vista compartida del horario semanal (normal o láser).
 *
 * Orquesta la pantalla de horario para los dos modos: el normal (`schedule.html`) y
 * el láser (`laser_schedule.html`), que comparten estructura (rejilla + navegación +
 * leyenda + modal de slot) y solo difieren en el endpoint, el permiso y el texto.
 * `ScheduleScreen` y `LaserScheduleScreen` la renderizan con sus props.
 *
 * Presentacional + hooks; sin `fetch` (`docs/frontend_architecture.md`, Pr. 2). La
 * rejilla llega de `useSchedule` (TanStack Query), cacheada por sede + modo + semana;
 * la sede activa viaja por `X-Sede-Id` (lib/api). El permiso lo comprueba
 * `RequireSchedulePermission` (paridad con el SSR). El estado local es el
 * desplazamiento de semana y el slot abierto (UI pura, no dato de servidor).
 */
import { useState } from "react";

import { useAuth } from "@/lib/auth";

import { schedulingErrorMessage, weekRangeLabel } from "../format";
import { useSchedule } from "../hooks/useScheduling";
import { RequireSchedulePermission } from "./RequireSchedulePermission";
import { ScheduleGrid } from "./ScheduleGrid";
import { ScheduleLegend } from "./ScheduleLegend";
import { SlotOrdersDialog, type OpenSlot } from "./SlotOrdersDialog";
import { WeekNavigator } from "./WeekNavigator";

interface ScheduleViewProps {
  /** Título de la pantalla (p. ej. "Horario semanal"). */
  title: string;
  /** Permiso exigido (`schedule` o `laser_schedule`). */
  permission: string;
  /** Modo láser: elige los endpoints y ajusta los textos. */
  laser: boolean;
}

/** Roles a los que el SSR mostraba el monto de venta en el detalle del slot. */
const AMOUNT_ROLES = ["admin", "supervisor"];

export function ScheduleView({ title, permission, laser }: ScheduleViewProps) {
  const { hasPermission, user } = useAuth();
  const canSee = hasPermission(permission);
  const showAmount = user !== null && AMOUNT_ROLES.includes(user.role);

  // Estado de UI local (no dato de servidor): semana visible y slot abierto.
  const [weekOffset, setWeekOffset] = useState(0);
  const [slot, setSlot] = useState<OpenSlot | null>(null);

  // La rejilla solo se pide con el permiso (gating); la sede activa la añade lib/api.
  const query = useSchedule(weekOffset, laser, canSee);

  return (
    <RequireSchedulePermission permission={permission} title={title}>
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>

        <p className="text-sm text-slate-500">
          {laser
            ? "Muestra solo las órdenes láser. Las órdenes normales tienen su propio horario."
            : "Muestra solo las órdenes normales. Las órdenes láser tienen su propio horario."}
        </p>

        <WeekNavigator
          rangeLabel={weekRangeLabel(query.data?.week_start ?? null, query.data?.week_end ?? null)}
          onPrev={() => setWeekOffset((offset) => offset - 1)}
          onNext={() => setWeekOffset((offset) => offset + 1)}
        />

        <ScheduleLegend laser={laser} />

        {query.isPending ? (
          <p className="py-10 text-center text-sm text-slate-500" role="status">
            Cargando horario…
          </p>
        ) : query.isError ? (
          <p className="py-10 text-center text-sm text-red-600" role="alert">
            {/* El mensaje del backend (p. ej. sede faltante) se muestra tal cual. */}
            {schedulingErrorMessage(query.error)}
          </p>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-2">
            <ScheduleGrid
              rows={query.data.schedule_data}
              onOpenSlot={(date, time) => setSlot({ date, time })}
            />
          </div>
        )}

        <SlotOrdersDialog
          slot={slot}
          laser={laser}
          showAmount={showAmount}
          onClose={() => setSlot(null)}
        />
      </div>
    </RequireSchedulePermission>
  );
}
